import { useCallback, useEffect } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Column, PulseCard, Row } from '~/commonComponents';
import { useToggleShortlistCandidate } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import { OutlineBookmarkIcon, SolidBookmarkIcon } from '@tempmee/react-icons';

interface IShortlistProfessionalProps {
  isShortlisted: boolean;
  professional: {
    firstName: string;
    id: string;
  };
}

export const ShortlistProfessional = ({
  isShortlisted: inputIsShortlisted,
  professional,
}: IShortlistProfessionalProps) => {
  const [isShortlisted, controls] = useToggle(inputIsShortlisted);

  const { mutate: doShortlistCandidate } = useToggleShortlistCandidate({
    candidateId: professional.id,
    isShortlisted,
  });

  useEffect(() => {
    controls.set(inputIsShortlisted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIsShortlisted]);

  const handleShortlistClick = useCallback(() => {
    doShortlistCandidate();
    setTimeout(() => {
      controls.setOn();
    });
  }, [controls, doShortlistCandidate]);

  const handleUndoShortlistClick = useCallback(() => {
    doShortlistCandidate();
    setTimeout(() => {
      controls.setOff();
    });
  }, [controls, doShortlistCandidate]);

  return (
    <PulseCard
      ActionComponent={() =>
        isShortlisted ? (
          <Column alignItems="center">
            <Row
              alignItems="center"
              gap={8}
              justifyContent="center"
              paddingBlock={11}
              paddingInline={24}
            >
              <SolidBookmarkIcon />
              <Typography as="span" bold color={colors.secondary[500]} size="body_large">
                Shortlisted
              </Typography>
            </Row>
            <Typography as="span" id="btn-undo-shortlist" onClick={handleUndoShortlistClick}>
              Undo Shortlist
            </Typography>
          </Column>
        ) : (
          <Row
            alignItems="center"
            backgroundColor={colors.secondary[500]}
            borderRadius={25}
            className="shadow"
            cursor="pointer"
            gap={8}
            justifyContent="center"
            paddingBlock={11}
            paddingInline={24}
            width={262}
            style={{
              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.15), 0 6px 10px 2px rgba(0, 0, 0, 0.15)',
            }}
            onClick={handleShortlistClick}
          >
            <OutlineBookmarkIcon color={colors.shades[0]} />
            <Typography as="span" bold color={colors.shades[0]} size="body_large">
              Shortlist Professional
            </Typography>
          </Row>
        )
      }
      isItalic={!isShortlisted}
      ComponentProps={{
        BoxProps: isShortlisted
          ? {
              backgroundColor: '#F2FBFB',
              borderColor: colors.secondary[500],
              borderWidth: 1.5,
            }
          : {},
      }}
      description={
        isShortlisted ? (
          `${professional.firstName} has been shortlisted as a Candidate for this job.`
        ) : (
          <>
            Shortlisted professionals will be displayed at top in the Candidates tab
            <br />
            of Manage Jobs.
          </>
        )
      }
      title={isShortlisted ? 'Congrats!' : 'Would you like to shortlist this professional?'}
    />
  );
};
